import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useMyContext } from '../../../../context/MyContext';
import { FiRefreshCw, FiMail, FiCheckCircle, FiTrendingUp, FiAlertCircle } from 'react-icons/fi';
import {
  Mail,
  Send,
  Eye,
  MousePointerClick,
  RefreshCw
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer
} from "recharts";

const ZohoDashboard = () => {
  const { isDarkMode } = useMyContext();

  const [loading, setLoading] = useState(true);
  const [syncLoading, setSyncLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [campaigns, setCampaigns] = useState([]);

  const [dashboardStats, setDashboardStats] = useState({
    total_emails: 0,
    total_delivered: 0,
    open_rate: 0,
    click_rate: 0,
    bounced: 0,
    subscribers: 0,
  });

  const campaignsPerPage = 5;

  // ==================== Fetch Zoho Dashboard Data ====================
  const fetchZohoDashboard = async () => {
    try {
      setLoading(true);

      // TODO: Replace with actual Zoho API call
      // const cus_id = localStorage.getItem("cus_id");
      // const token = localStorage.getItem("token");
      // const res = await fetch(`${Base_Url}${zohoDashboardEndpoint}`, {
      //   method: "GET",
      //   headers: { "Content-Type": "application/json", cus_id, token },
      // });
      // const data = await res.json();

      // Temporary placeholder data
      setTimeout(() => {
        setDashboardStats({
          total_emails: 12480,
          total_delivered: 11960,
          open_rate: 42.6,
          click_rate: 8.3,
          bounced: 520,
          subscribers: 3240,
        });

        setCampaigns([
          { id: 'ZC-1042', name: 'Diwali Offer Blast', sent: 4200, opened: 1890, status: 'Completed', date: '2026-07-20' },
          { id: 'ZC-1041', name: 'Weekly Newsletter', sent: 3600, opened: 1420, status: 'Completed', date: '2026-07-18' },
          { id: 'ZC-1040', name: 'New Feature Launch', sent: 2900, opened: 1105, status: 'Scheduled', date: '2026-07-25' },
          { id: 'ZC-1039', name: 'Abandoned Cart Reminder', sent: 1780, opened: 640, status: 'Completed', date: '2026-07-15' },
          { id: 'ZC-1038', name: 'Monsoon Sale', sent: 5100, opened: 2210, status: 'Completed', date: '2026-07-10' },
        ]);

        setLoading(false);
      }, 600);

    } catch (err) {
      console.error("Zoho Dashboard Error:", err);
      setLoading(false);
    }
  };

  const syncZohoData = async () => {
    try {
      setSyncLoading(true);
      // TODO: Replace with actual Zoho sync API call
      await new Promise((resolve) => setTimeout(resolve, 1200));
      await fetchZohoDashboard();
    } catch (err) {
      console.error("Zoho Sync Error:", err);
    } finally {
      setSyncLoading(false);
    }
  };

  useEffect(() => {
    fetchZohoDashboard();
  }, []);

  const totalPages = Math.ceil(campaigns.length / campaignsPerPage);

  const currentCampaigns = campaigns.slice(
    (currentPage - 1) * campaignsPerPage,
    currentPage * campaignsPerPage
  );

  // ==================== LOADING STATE ====================
  if (loading) {
    return (
      <div className={`h-screen flex items-center justify-center ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
        <div className="flex flex-col items-center gap-4">
          <FiRefreshCw className="animate-spin text-purple-600" size={48} />
          <p className="text-gray-500 font-medium">Loading Zoho dashboard...</p>
        </div>
      </div>
    );
  }

  // ==================== MAIN DASHBOARD ====================
  return (
    <div className={`flex flex-col ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
      <div className="bg-purple-50 p-4 lg:p-4 relative">
        {/* Background Glow */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-purple-200 rounded-full blur-3xl opacity-30" />
          <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-fuchsia-200 rounded-full blur-3xl opacity-30" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">

          {/* Header */}
          <motion.div
            className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div>
              {/* <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-3">
                <Mail size={14} /> Zoho Mail Integration
              </div> */}
              <h1 className="text-xl font-bold text-white bg-purple-600 inline-block px-2.5 py-1 rounded-md">Zoho Dashboard</h1>
              <p className="text-gray-500 mt-1">
                Track your email campaigns &amp; engagement in real-time
              </p>
            </div>

            <button
              onClick={syncZohoData}
              disabled={syncLoading}
              className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white px-6 py-3 rounded-2xl font-medium transition-all shadow-lg active:scale-95 disabled:opacity-70"
            >
              <RefreshCw className={`w-5 h-5 ${syncLoading ? 'animate-spin' : ''}`} />
              {syncLoading ? 'Syncing...' : 'Sync Zoho Data'}
            </button>
          </motion.div>

          {/* Stat Cards */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {[
              { icon: Send, label: "Emails Sent", value: dashboardStats.total_emails.toLocaleString(), color: "purple" },
              { icon: Mail, label: "Delivered", value: dashboardStats.total_delivered.toLocaleString(), color: "indigo" },
              { icon: Eye, label: "Open Rate", value: `${dashboardStats.open_rate}%`, color: "fuchsia" },
              { icon: MousePointerClick, label: "Click Rate", value: `${dashboardStats.click_rate}%`, color: "violet" },
            ].map((item, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -6, scale: 1.02 }}
                className="group relative overflow-hidden rounded-2xl p-5 border border-gray-300 shadow-[0_8px_32px_rgba(147,51,234,0.15)] hover:shadow-[0_12px_40px_rgba(147,51,234,0.3)] transition-all duration-300 bg-gradient-to-br from-purple-100/60 via-white/30 to-purple-100/40 backdrop-blur-2xl"
              >
                {/* Shine overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                {/* Glow ring on hover */}
                <div className={`absolute -top-10 -right-10 w-32 h-32 bg-${item.color}-400/30 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                <div className="relative flex items-center justify-between">
                  <div className={`p-2.5 bg-gradient-to-br from-blue-200/10 to-blue-200/50 backdrop-blur-sm rounded-xl border border-gray-300 shadow-sm`}>
                    <item.icon className={`w-7 h-7 text-${item.color}-600`} />
                  </div>
                </div>
                <div className="relative mt-3">
                  <p className="text-gray-600 text-sm tracking-wide">{item.label}</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-2 drop-shadow-sm">{item.value}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Chart + Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
            <motion.div
              className="lg:col-span-2 bg-white rounded-3xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
            >
              <h2 className="text-xl font-semibold mb-6 text-gray-900">Email Opens (Last 7 Days)</h2>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={[
                    { name: "Mon", opens: 320 }, { name: "Tue", opens: 410 },
                    { name: "Wed", opens: 280 }, { name: "Thu", opens: 510 },
                    { name: "Fri", opens: 640 }, { name: "Sat", opens: 390 },
                    { name: "Sun", opens: 470 }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="opens" stroke="#9333EA" strokeWidth={4} dot={{ r: 5, fill: "#9333EA" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            <motion.div
              className="bg-white rounded-3xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow flex flex-col"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="text-xl font-semibold mb-6 text-gray-900">Recent Activity</h2>
              <div className="space-y-5 flex-1 text-sm">
                {[
                  "Campaign 'Diwali Offer Blast' completed",
                  "New subscriber joined mailing list",
                  "Campaign 'Feature Launch' scheduled"
                ].map((act, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <div className="text-2xl mt-0.5">📧</div>
                    <div className="text-gray-700">{act}</div>
                  </div>
                ))}
              </div>
              <button className="mt-6 w-full py-3 border border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-2xl font-medium text-gray-700 transition-all">
                View All Activity
              </button>
            </motion.div>
          </div>

          {/* Campaigns Table */}
          <motion.div
            className="mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">Recent Campaigns</h2>
              <p className="text-gray-500 text-sm">Latest campaigns from Zoho</p>
            </div>

            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-200 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Campaign ID</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Name</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Sent</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Opened</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {currentCampaigns.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-6 py-5 font-medium text-gray-900">#{c.id}</td>
                      <td className="px-6 py-5 text-gray-700">{c.name}</td>
                      <td className="px-6 py-5 text-gray-700">{c.sent.toLocaleString()}</td>
                      <td className="px-6 py-5 text-gray-700">{c.opened.toLocaleString()}</td>
                      <td className="px-6 py-5">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full 
                          ${c.status === 'Completed' ? 'bg-green-100 text-green-700' :
                            c.status === 'Scheduled' ? 'bg-blue-100 text-blue-700' :
                              'bg-amber-100 text-amber-700'}`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-gray-500 text-sm">{c.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex items-center justify-between  py-2 border-t bg-gray-200">
                <button
                  onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-5 py-2 border rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-white transition"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page <span className="font-semibold">{currentPage}</span> of {totalPages || 1}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="px-5 py-2 border rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-white transition"
                >
                  Next
                </button>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default ZohoDashboard;